/**
 * 将 API 返回的 componentList 转换为 PANEL_SIZE_PRESETS 格式。
 *
 * componentType → DRAW_TYPE 映射：
 *   HOLE → keyHole, INNER_HOLE → perforation, STAND → liPai, STAND_HOLE → baseHole
 */

import { DRAW_TYPE, DEFAULT_CONFIG } from './constants.js';

/** componentType → DRAW_TYPE 映射表 */
var COMPONENT_TYPE_MAP = {
  HOLE: DRAW_TYPE.KEY_HOLE,
  INNER_HOLE: DRAW_TYPE.PERFORATION,
  STAND: DRAW_TYPE.LI_PAI,
  STAND_HOLE: DRAW_TYPE.BASE_HOLE
};

/**
 * 解析 attr JSON 字符串为 geometryConfig。
 * @param {string} componentType - API 的 componentType
 * @param {string} attrJson - JSON 字符串
 * @returns {Object} geometryConfig
 */
function parseAttrToGeometry(componentType, attrJson) {
  var attr = {};
  try {
    attr = JSON.parse(attrJson) || {};
  } catch (e) {
    attr = {};
  }

  switch (componentType) {
    case 'HOLE':
      return {
        outerHoleDiameterMm: parseFloat(attr.outer_diameter) || 0,
        innerHoleDiameterMm: parseFloat(attr.inner_diameter) || 0
      };
    case 'INNER_HOLE':
      return {
        radiusMm: parseFloat(attr.radius) || 0
      };
    case 'STAND':
      return {
        slotWidthMm: parseFloat(attr.width) || 0,
        slotHeightMm: parseFloat(attr.height) || 0,
        slotRange:
          parseFloat(attr.slotRange) ||
          DEFAULT_CONFIG.liPaiSlotRangePercent / 100
      };
    case 'STAND_HOLE':
      // radius 字段暂不转换，预留
      return {
        widthMm: parseFloat(attr.width) || 0,
        heightMm: parseFloat(attr.height) || 0
      };
    default:
      return {};
  }
}

/**
 * 根据 componentType 和 attr 生成唯一 id。
 * @param {string} componentType
 * @param {Object} attr - 已解析的 attr 对象
 * @returns {string}
 */
function generatePresetId(componentType, attr) {
  switch (componentType) {
    case 'HOLE':
      return 'kh-' + attr.inner_diameter + '-' + attr.outer_diameter;
    case 'INNER_HOLE':
      return 'pf-' + attr.radius;
    case 'STAND':
      return 'lp-' + attr.width + '-' + attr.height;
    case 'STAND_HOLE':
      return 'bh-' + attr.width + '-' + attr.height;
    default:
      return 'unknown';
  }
}

/**
 * 将 API 返回的 componentList 转换为页面可用的预设数据。
 *
 * @param {Array} componentList - skuTemplate.componentList
 * @returns {{
 *   presets: Object.<string, Array>,
 *   maxCount: Object.<string, number>,
 *   minCount: Object.<string, number>
 * }}
 */
export function buildPresetsFromApi(componentList) {
  var presets = {};
  var maxCount = {};
  var minCount = {};

  if (!componentList || !componentList.length) {
    return { presets: presets, maxCount: maxCount, minCount: minCount };
  }

  var i, j, group, drawType, item, attr, list;
  for (i = 0; i < componentList.length; i++) {
    group = componentList[i];
    drawType = COMPONENT_TYPE_MAP[group.componentType];
    if (!drawType) continue;

    maxCount[drawType] = group.maxQty;
    minCount[drawType] = group.minQty;

    list = [];
    if (group.list && group.list.length) {
      for (j = 0; j < group.list.length; j++) {
        item = group.list[j];
        attr = {};
        try {
          attr = JSON.parse(item.attr) || {};
        } catch (e) {
          attr = {};
        }

        list.push({
          id: generatePresetId(group.componentType, attr),
          label: item.componentName || '',
          isDefault: j === 0,
          thumbSrc: item.icon || '',
          helpUrl: item.helpUrl || '',
          geometryConfig: parseAttrToGeometry(group.componentType, item.attr)
        });
      }
    }
    presets[drawType] = list;
  }
  return { presets: presets, maxCount: maxCount, minCount: minCount };
}

/**
 * 将自定义组件导出的 componentData 转换为 acrylic 数据格式。
 * 用于提交时将多部件数据转为后端接口需要的 acrylic 字段。
 *
 * @param {Object} componentData - layerData.componentData
 * @param {Array<Array<number>>} componentData.keyPointList - 钥匙扣比例坐标 [[x, y], ...]
 * @param {Array<Object>} componentData.originCenterPointList - 钥匙扣元数据（含 geometryConfig）
 * @param {Array<Object>} componentData.socketInfoList - 立牌信息（含 slotRect 坐标比例）
 * @param {Array<Object>} componentData.originSocketInfoList - 立牌元数据（含 geometryConfig.slotWidthMm/slotHeightMm）
 * @param {Array<Array<number>>} componentData.perforationPointList - 穿孔比例坐标 [[x, y], ...]
 * @param {Array<Object>} componentData.originPerforationPointList - 穿孔元数据（含 geometryConfig）
 * @param {Array<Object>} componentData.baseHoleInfoList - 底座孔信息
 * @param {Array<Object>} componentData.originBaseHoleInfoList - 底座孔元数据（含 geometryConfig）
 * @returns {Object} acrylic 对象，含 edgeHoles/slotRect/innerHoles/slotHoles
 *
 * @example
 * var acrylic = convertComponentDataToAcrylic(layerData.componentData);
 * // acrylic = {
 * //   edgeHoles: [[0.5, 0.08, 1.25, 3]],
 * //   slotRect: [0.35, 0.85, 10, 2.4],
 * //   innerHoles: [[0.5, 0.5, 2.5]],
 * //   slotHoles: [[0.5, 0.5, 0.785, 12, 2.4]]
 * // }
 */
export function convertComponentDataToAcrylic(componentData) {
  if (!componentData) return {};

  var acrylic = {};

  // 钥匙扣 → edgeHoles: [[x, y, innerR, outerR], ...]  x,y 比例(0~1), innerR/outerR mm
  var origins;
  if (componentData.keyPointList && componentData.keyPointList.length) {
    origins = componentData.originCenterPointList || [];
    acrylic.edgeHoles = componentData.keyPointList.map(function (pt, idx) {
      var origin = origins[idx];
      var gc = origin && origin.geometryConfig;
      var innerR = gc ? gc.innerHoleDiameterMm / 2 : 0;
      var outerR = gc ? gc.outerHoleDiameterMm / 2 : 0;
      return [pt[0], pt[1], innerR, outerR];
    });
  }

  // 立牌 → slotRect: [x, y, slotWidth, slotHeight]
  // x/y 来自 rectCanvasToSlotRect（比例），slotWidth/slotHeight 来自 geometryConfig（mm）
  if (componentData.socketInfoList && componentData.socketInfoList.length) {
    var first = componentData.socketInfoList[0];
    if (first.slotRect) {
      var originSocket = (componentData.originSocketInfoList || [])[0];
      var gc = originSocket && originSocket.geometryConfig;
      var slotW = gc ? gc.slotWidthMm : 0;
      var slotH = gc ? gc.slotHeightMm : 0;
      acrylic.slotRect = [first.slotRect[0], first.slotRect[1], slotW, slotH];
    }
  }

  // 穿孔 → innerHoles: [[x, y, r], ...]  x,y 比例(0~1), r mm
  if (
    componentData.perforationPointList &&
    componentData.perforationPointList.length
  ) {
    origins = componentData.originPerforationPointList || [];
    acrylic.innerHoles = componentData.perforationPointList.map(function (
      pt,
      idx
    ) {
      var origin = origins[idx];
      var r =
        origin && origin.geometryConfig ? origin.geometryConfig.radiusMm : 0;
      return [pt[0], pt[1], r];
    });
  }

  // 底座孔 → slotHoles: [[x, y, angle, slotWidth, slotHeight], ...]
  // thickness 字段废弃
  if (componentData.baseHoleInfoList && componentData.baseHoleInfoList.length) {
    origins = componentData.originBaseHoleInfoList || [];
    acrylic.slotHoles = componentData.baseHoleInfoList.map(function (
      info,
      idx
    ) {
      var origin = origins[idx];
      // slotWidth/slotHeight 来自 STAND_HOLE attr 的 width/height 字段（mm），对应 geometryConfig
      var slotWidth =
        origin && origin.geometryConfig ? origin.geometryConfig.widthMm : 0;
      var slotHeight =
        origin && origin.geometryConfig ? origin.geometryConfig.heightMm : 0;
      return [
        info.centerPoint.x,
        info.centerPoint.y,
        info.rotationRad,
        slotWidth,
        slotHeight
      ];
    });
  }

  return acrylic;
}
